import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useEffect, useState } from "react";
import { useNotify } from "@imminently/react/store/notifications";
import { useTranslation } from "react-i18next";

import type { ExternalRegisterAlertType } from "@/components/externalregister/types";

import { compactTableSx } from "../shared/ExternalRegisterSettingsUi";
import { useExternalRegisterAdmin } from "./externalRegisterAdminProvider";
import {
  getDefaultAlertTypeColor,
  getDefaultAlertTypeLabel,
} from "./externalRegisterAdminHelpers";
import { EXTERNAL_REGISTER_ADMIN_ALERT_TYPES } from "./externalRegisterAdminTypes";
import {
  compactFieldSx,
  panelSx,
} from "./externalRegisterAdminUi";

export const ExternalRegisterSettingsPanel = () => {
  const { t } = useTranslation();
  const notify = useNotify();
  const { settings, loadingSettings, saving, updateSettings, refreshSettings } = useExternalRegisterAdmin();
  const [labels, setLabels] = useState<Record<ExternalRegisterAlertType, string>>({} as Record<ExternalRegisterAlertType, string>);
  const [colors, setColors] = useState<Record<ExternalRegisterAlertType, string>>({} as Record<ExternalRegisterAlertType, string>);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setLabels(EXTERNAL_REGISTER_ADMIN_ALERT_TYPES.reduce((acc, type) => ({
      ...acc,
      [type]: settings.alertTypes[type]?.label?.trim() || getDefaultAlertTypeLabel(type),
    }), {} as Record<ExternalRegisterAlertType, string>));
    setColors(EXTERNAL_REGISTER_ADMIN_ALERT_TYPES.reduce((acc, type) => ({
      ...acc,
      [type]: settings.alertTypes[type]?.color?.trim() || getDefaultAlertTypeColor(type),
    }), {} as Record<ExternalRegisterAlertType, string>));
  }, [settings]);

  const markDirty = () => setSaved(false);

  const saveSettings = async () => {
    try {
      await updateSettings({
        alertTypes: EXTERNAL_REGISTER_ADMIN_ALERT_TYPES.reduce((acc, type) => ({
          ...acc,
          [type]: {
            label: labels[type]?.trim() || getDefaultAlertTypeLabel(type),
            color: colors[type]?.trim() || getDefaultAlertTypeColor(type),
          },
        }), {}),
      });
      setSaved(true);
      notify.success(t("externalRegister.admin.settings.toast.saved"));
      window.setTimeout(() => setSaved(false), 3000);
    } catch {
      setSaved(false);
      notify.error(t("externalRegister.admin.settings.toast.error"));
    }
  };

  return (
    <Stack spacing={2}>
      <Card variant="outlined" sx={panelSx}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {t("externalRegister.admin.settings.title")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("externalRegister.admin.settings.description")}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={loadingSettings || saving}
                  onClick={() => {
                    markDirty();
                    setLabels(EXTERNAL_REGISTER_ADMIN_ALERT_TYPES.reduce((acc, type) => ({
                      ...acc,
                      [type]: getDefaultAlertTypeLabel(type),
                    }), {} as Record<ExternalRegisterAlertType, string>));
                    setColors(EXTERNAL_REGISTER_ADMIN_ALERT_TYPES.reduce((acc, type) => ({
                      ...acc,
                      [type]: getDefaultAlertTypeColor(type),
                    }), {} as Record<ExternalRegisterAlertType, string>));
                  }}
                >
                  {t("externalRegister.admin.settings.buttons.suggestedDefaults")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    markDirty();
                    void refreshSettings();
                  }}
                  disabled={loadingSettings || saving}
                >
                  {t("externalRegister.admin.settings.buttons.reloadSettings")}
                </Button>
              </Stack>
            </Stack>

            {loadingSettings ? (
              <Alert
                severity="info"
                variant="outlined"
                icon={<CircularProgress size={18} />}
                sx={{ alignItems: "center", py: 0.75 }}
              >
                {t("externalRegister.admin.settings.loadingAlert")}
              </Alert>
            ) : null}

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
              <Table
                size="small"
                sx={{
                  ...compactTableSx,
                  "& th": {
                    ...compactTableSx["& th"],
                    py: 0.5,
                  },
                  "& td": {
                    ...compactTableSx["& td"],
                    py: 0.45,
                    verticalAlign: "middle",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 220 }}>{t("externalRegister.admin.settings.table.headers.alertType")}</TableCell>
                    <TableCell>{t("externalRegister.admin.settings.table.headers.label")}</TableCell>
                    <TableCell sx={{ width: 210 }}>{t("externalRegister.admin.settings.table.headers.colour")}</TableCell>
                    <TableCell sx={{ width: 190 }}>{t("externalRegister.admin.settings.table.headers.preview")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {EXTERNAL_REGISTER_ADMIN_ALERT_TYPES.map((type) => (
                    <TableRow key={type}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {type}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t("externalRegister.admin.settings.table.defaultPrefix")}{getDefaultAlertTypeLabel(type)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={labels[type] ?? ""}
                          onChange={(event) => {
                            markDirty();
                            setLabels((current) => ({ ...current, [type]: event.target.value }));
                          }}
                          disabled={loadingSettings || saving}
                          fullWidth
                          autoComplete="new-password"
                          inputProps={{
                            "aria-label": `${type} label`,
                            autoComplete: "new-password",
                            name: `external-register-${type.toLowerCase().replace(/_/g, "-")}-label`,
                          }}
                          placeholder={getDefaultAlertTypeLabel(type)}
                          sx={compactFieldSx}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            key={`${type}-${colors[type] || getDefaultAlertTypeColor(type)}`}
                            component="input"
                            type="color"
                            aria-label={`${type} colour`}
                            defaultValue={colors[type] || getDefaultAlertTypeColor(type)}
                            disabled={loadingSettings || saving}
                            onBlur={(event) => {
                              markDirty();
                              setColors((current) => ({ ...current, [type]: event.target.value }));
                            }}
                            sx={{
                              width: 36,
                              height: 32,
                              p: 0.25,
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              backgroundColor: "background.paper",
                              cursor: loadingSettings || saving ? "default" : "pointer",
                            }}
                          />
                          <TextField
                            size="small"
                            value={colors[type] ?? ""}
                            onChange={(event) => {
                              markDirty();
                              setColors((current) => ({ ...current, [type]: event.target.value }));
                            }}
                            disabled={loadingSettings || saving}
                            autoComplete="new-password"
                            inputProps={{
                              "aria-label": `${type} hex colour`,
                              autoComplete: "new-password",
                              name: `external-register-${type.toLowerCase().replace(/_/g, "-")}-hex-colour`,
                            }}
                            placeholder={getDefaultAlertTypeColor(type)}
                            sx={{ ...compactFieldSx, width: 120 }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={labels[type] || getDefaultAlertTypeLabel(type)}
                          sx={{
                            backgroundColor: colors[type] || getDefaultAlertTypeColor(type),
                            color: "#fff",
                            fontWeight: 700,
                            maxWidth: 170,
                            "& .MuiChip-label": {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
              {saved ? (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "success.main" }}>
                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {t("externalRegister.admin.settings.table.saved")}
                  </Typography>
                </Stack>
              ) : null}
              <Button
                variant="contained"
                onClick={saveSettings}
                disabled={!settings || loadingSettings || saving}
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
              >
                {saving ? t("common.buttons.saving") : loadingSettings ? t("externalRegister.admin.settings.buttons.fetching") : t("externalRegister.admin.settings.buttons.saveSettings")}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

    </Stack>
  );
};
